/**
 * /app/$tenantSlug/me/tax — Me → Pay → Tax: my income-tax declaration
 * (employee self-service, regime-gated).
 *
 * Navigation rebuild (wave 3, 28 Oct 2026): moved from
 * /payroll/tax-declaration, which now redirects here (stored notification
 * links point at it). The HR queue stays at /payroll/tax-declaration/queue
 * and the HR review at /payroll/tax-declaration/{id}.
 *
 * REGIME-AWARENESS (the central design fact, §115BAC):
 *   - FY selector → regime chooser card FIRST.
 *   - NEW regime: a clear explainer + NO declaration form (only the standard
 *     deduction + employer NPS apply to TDS — neither is declared).
 *   - OLD regime: the Form-12BB-style declaration form (per-head sections with
 *     inline cap hints), submit, then the proof-upload accordion per item,
 *     then submit-for-review.
 *
 * Backend boundary: /api/v1/me/tax-declaration/* (RequireAuth; strict OWN
 * ownership). This page never sends employee_id — the server derives it.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, FileText } from "lucide-react";
import {
  useMyDeclaration,
  useSetMyRegime,
  useSetMyItems,
  useSubmitMyDeclaration,
  useSubmitMyProofs,
  useUploadProof,
  useDeleteProof,
} from "@/hooks/use-tax-declaration";
import {
  DECLARATION_HEADS,
  type DeclarationHead,
  type DeclarationItem,
  type UpsertItemInput,
} from "@/types/tax-declaration";
import { MeModuleShell } from "@/components/shared/me-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { GlossaryTerm } from "@/components/shared/glossary-term";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { formatPaise, rupeesToPaise, paiseToRupeeInput } from "@/lib/expense-format";
import { toApiError } from "@/lib/api/client";
import { regimeCopy, type RegimeCopy } from "@/lib/tax-regime-copy";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/me/tax")({
  component: MyTaxDeclarationRoute,
});

// Default FY = the current Indian FY start year (Apr-Mar). Before April, the
// active FY started the previous calendar year.
function currentFYStartYear(): number {
  const now = new Date();
  const m = now.getMonth(); // 0=Jan
  return m >= 3 ? now.getFullYear() : now.getFullYear() - 1;
}

function fyLabel(fy: number): string {
  return `FY ${fy}-${String((fy + 1) % 100).padStart(2, "0")}`;
}

function MyTaxDeclarationRoute() {
  const [fy, setFy] = useState(currentFYStartYear());
  // A fixed 5-year lookback ending at the current FY (newest first), so
  // older declarations stay viewable — mirrors the leave-balance year window.
  const fyOptions = Array.from(
    { length: 5 },
    (_, i) => currentFYStartYear() - i,
  );
  const { data, isLoading, isError, error } = useMyDeclaration(fy);
  const copy = data
    ? regimeCopy({
        regime: data.regime.regime,
        locked: data.regime.locked,
        lockReason: data.regime.lock_reason ?? "",
        chosenByYou: data.regime.chosen_by_you ?? false,
      })
    : null;

  return (
    <MeModuleShell
      title="Tax"
      description="Pick your tax regime for the year, then (under the old regime) declare your planned investments and upload proofs."
    >
      <div className="mb-4 flex items-center gap-3">
        <Label htmlFor="fy-select" className="text-sm font-medium">
          Financial Year
        </Label>
        <select
          id="fy-select"
          className="h-9 pointer-coarse:h-11 rounded-md border border-input bg-background px-3 text-sm"
          value={fy}
          onChange={(e) => setFy(Number(e.target.value))}
        >
          {fyOptions.map((y) => (
            <option key={y} value={y}>
              {fyLabel(y)}
            </option>
          ))}
        </select>
      </div>

      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      )}
      {isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn&apos;t load your declaration</AlertTitle>
          <AlertDescription>{toApiError(error).message}</AlertDescription>
        </Alert>
      )}
      {data && (
        <RegimeChooser
          fy={fy}
          regime={data.regime.regime}
          locked={data.regime.locked}
          copy={copy}
        />
      )}
      {data && data.regime.regime === "old" && (
        <OldRegimeWorkflow fy={fy} declaration={data.declaration} />
      )}
      {data && data.regime.regime === "new" && (
        <NewRegimeExplainer showSwitchHint={copy?.showSwitchHint ?? false} />
      )}
    </MeModuleShell>
  );
}

function RegimeChooser({
  fy,
  regime,
  locked,
  copy,
}: {
  fy: number;
  regime: "new" | "old";
  locked: boolean;
  copy: RegimeCopy | null;
}) {
  const setRegime = useSetMyRegime(fy);
  const choose = (next: "new" | "old") => {
    if (next === regime) return;
    setRegime.mutate(next, {
      onSuccess: () => toast.success(`Switched to the ${next} regime`),
      onError: (e) => {
        const err = toApiError(e);
        toast.error(
          err.error === "regime_locked"
            ? "Your regime is locked for this year (a payroll run has been processed). Ask HR to re-open it."
            : err.message,
        );
      },
    });
  };
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-base">Tax Regime — {fyLabel(fy)}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {copy?.lockNotice && (
          <p
            className="rounded-md border bg-muted/50 px-3 py-2 text-sm"
            data-testid="regime-lock-notice"
          >
            {copy.lockNotice}
          </p>
        )}
        {copy?.appliedNote && (
          <p className="text-sm text-muted-foreground" data-testid="regime-applied-note">
            {copy.appliedNote}
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <RegimeCard
            value="new"
            selected={regime === "new"}
            disabled={locked || setRegime.isPending}
            dimmed={locked}
            onChoose={() => choose("new")}
            title={
              <>
                New regime (default)
                <GlossaryTerm term="NewRegime" className="relative z-10 -m-1.5 size-7 shrink-0 justify-center pointer-coarse:m-0 pointer-coarse:size-11" />
              </>
            }
          >
            Lower slab rates. No investment deductions — only the ₹75,000
            standard deduction + employer NPS apply.
          </RegimeCard>
          <RegimeCard
            value="old"
            selected={regime === "old"}
            disabled={locked || setRegime.isPending}
            dimmed={locked}
            onChoose={() => choose("old")}
            title={
              <>
                Old regime
                <GlossaryTerm term="OldRegime" className="relative z-10 -m-1.5 size-7 shrink-0 justify-center pointer-coarse:m-0 pointer-coarse:size-11" />
              </>
            }
          >
            Declare{" "}
            <span className="inline-flex items-center gap-0.5">
              §80C
              <GlossaryTerm term="Sec80C" className="relative z-10 -m-1.5 size-7 shrink-0 justify-center pointer-coarse:m-0 pointer-coarse:size-11" />
            </span>{" "}
            /{" "}
            <span className="inline-flex items-center gap-0.5">
              §80D
              <GlossaryTerm term="Sec80D" className="relative z-10 -m-1.5 size-7 shrink-0 justify-center pointer-coarse:m-0 pointer-coarse:size-11" />
            </span>{" "}
            /{" "}
            <span className="inline-flex items-center gap-0.5">
              HRA
              <GlossaryTerm term="HRA" className="relative z-10 -m-1.5 size-7 shrink-0 justify-center pointer-coarse:m-0 pointer-coarse:size-11" />
            </span>{" "}
            / home-loan interest etc. and upload proofs to reduce your TDS.
          </RegimeCard>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * One regime choice. The whole card is clickable through a "stretched"
 * button (absolute, covering the card) — the glossary help buttons sit
 * ABOVE it (relative z-10) as siblings, never inside it: a <button>
 * inside a <button> is invalid HTML and React warned about it on every
 * load (2026-10 final re-check).
 */
function RegimeCard({
  value,
  selected,
  disabled,
  dimmed,
  onChoose,
  title,
  children,
}: Readonly<{
  value: "new" | "old";
  selected: boolean;
  disabled: boolean;
  dimmed: boolean;
  onChoose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
}>) {
  const descId = `regime-${value}-desc`;
  return (
    <div
      className={`relative rounded-lg border p-4 text-left transition focus-within:ring-2 focus-within:ring-ring ${
        selected ? "border-primary ring-1 ring-primary" : "border-input"
      } ${dimmed ? "opacity-60" : "hover:border-primary/60"}`}
      data-testid={`regime-card-${value}`}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={onChoose}
        aria-pressed={selected}
        aria-label={value === "new" ? "Choose the new regime" : "Choose the old regime"}
        aria-describedby={descId}
        className="absolute inset-0 rounded-lg focus:outline-none disabled:cursor-not-allowed"
      />
      <p className="flex items-center gap-1.5 font-medium pointer-coarse:min-h-11">{title}</p>
      <p id={descId} className="mt-1 text-sm text-muted-foreground">
        {children}
      </p>
    </div>
  );
}

function NewRegimeExplainer({ showSwitchHint }: { showSwitchHint: boolean }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm text-muted-foreground">
          Under the new tax regime, investment declarations do not reduce your
          TDS. Only the automatic ₹75,000 standard deduction and your employer's
          NPS contribution (§80CCD(2)) apply. There's nothing to declare here.
        </p>
        {showSwitchHint && (
          <p className="mt-2 text-sm text-muted-foreground">
            If you'd prefer to claim deductions, switch to the old regime above
            (allowed once per financial year, before payroll is processed).
          </p>
        )}
      </CardContent>
    </Card>
  );
}

type DraftRow = {
  head: DeclarationHead;
  rupees: string;
  hraRentRupees: string;
  hraLandlordName: string;
  hraLandlordPan: string;
  hraIsMetro: boolean;
  /** 80TTA_TTB: the interest income earned this year (₹). */
  interestRupees: string;
};

function OldRegimeWorkflow({
  fy,
  declaration,
}: {
  fy: number;
  declaration: import("@/types/tax-declaration").Declaration | null;
}) {
  const status = declaration?.status ?? "draft";
  const editable = status === "draft";

  if (!editable && declaration) {
    return <ProofWorkflow fy={fy} declaration={declaration} />;
  }
  return <DeclarationForm fy={fy} declaration={declaration} />;
}

function DeclarationForm({
  fy,
  declaration,
}: {
  fy: number;
  declaration: import("@/types/tax-declaration").Declaration | null;
}) {
  const setItems = useSetMyItems(fy);
  const submit = useSubmitMyDeclaration(fy);

  const initial = useMemo<Record<DeclarationHead, DraftRow>>(() => {
    const base = {} as Record<DeclarationHead, DraftRow>;
    for (const { head } of DECLARATION_HEADS) {
      const existing = declaration?.items.find((i) => i.head === head);
      base[head] = {
        head,
        rupees: existing ? paiseToRupeeInput(existing.declared_paise) : "",
        hraRentRupees:
          existing?.hra_annual_rent_paise != null
            ? paiseToRupeeInput(existing.hra_annual_rent_paise)
            : "",
        hraLandlordName: existing?.hra_landlord_name ?? "",
        hraLandlordPan: "",
        hraIsMetro: existing?.hra_is_metro ?? false,
        // A claim declared before the interest field existed: its amount
        // WAS the interest figure.
        interestRupees:
          existing?.interest_income_paise != null
            ? paiseToRupeeInput(existing.interest_income_paise)
            : existing && head === "80TTA_TTB"
              ? paiseToRupeeInput(existing.declared_paise)
              : "",
      };
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declaration?.id]);

  const [rows, setRows] = useState<Record<DeclarationHead, DraftRow>>(initial);

  const update = (head: DeclarationHead, patch: Partial<DraftRow>) =>
    setRows((prev) => ({ ...prev, [head]: { ...prev[head], ...patch } }));

  const buildItems = (): UpsertItemInput[] | null => {
    const out: UpsertItemInput[] = [];
    for (const { head } of DECLARATION_HEADS) {
      const r = rows[head];
      const paise = r.rupees.trim() === "" ? 0 : rupeesToPaise(r.rupees);
      if (paise == null) {
        toast.error(`Invalid amount for ${head}`);
        return null;
      }
      if (head === "80TTA_TTB") {
        // The interest earned is what the claim rests on (§80A(2)): the
        // deduction is the interest up to the cap, so the claim follows it.
        const interest = r.interestRupees.trim() === "" ? 0 : rupeesToPaise(r.interestRupees);
        if (interest == null) {
          toast.error("Invalid interest income amount");
          return null;
        }
        if (interest === 0) continue;
        out.push({ head, declared_paise: interest, interest_income_paise: interest });
        continue;
      }
      if (paise === 0 && head !== "HRA") continue;
      const item: UpsertItemInput = { head, declared_paise: paise };
      if (head === "HRA") {
        const rent = r.hraRentRupees.trim() === "" ? 0 : rupeesToPaise(r.hraRentRupees);
        if (rent == null) {
          toast.error("Invalid HRA rent amount");
          return null;
        }
        if (rent === 0 && paise === 0) continue;
        item.hra_annual_rent_paise = rent;
        item.hra_landlord_name = r.hraLandlordName || null;
        item.hra_landlord_pan = r.hraLandlordPan || null;
        item.hra_is_metro = r.hraIsMetro;
      }
      out.push(item);
    }
    return out;
  };

  const save = (thenSubmit: boolean) => {
    const items = buildItems();
    if (!items) return;
    setItems.mutate(items, {
      onSuccess: (res) => {
        res.warnings.forEach((w) => toast.warning(w.message));
        toast.success("Declaration saved");
        if (thenSubmit) {
          submit.mutate(undefined, {
            onSuccess: () => toast.success("Submitted — you can now upload proofs"),
            onError: (e) => toast.error(toApiError(e).message),
          });
        }
      },
      onError: (e) => toast.error(toApiError(e).message),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Investment Declaration — {fyLabel(fy)}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {DECLARATION_HEADS.map(({ head, label, hint }) => (
          <div key={head} className="space-y-2 border-b pb-4 last:border-0">
            <div className="flex items-baseline justify-between gap-3">
              <Label htmlFor={`amt-${head}`}>{label}</Label>
            </div>
            <p className="text-xs text-muted-foreground">{hint}</p>
            {head === "80TTA_TTB" ? (
              <Input
                id={`amt-${head}`}
                inputMode="decimal"
                placeholder="Interest earned this year (₹)"
                value={rows[head].interestRupees}
                onChange={(e) => update(head, { interestRupees: e.target.value })}
                className="max-w-xs"
              />
            ) : (
              <Input
                id={`amt-${head}`}
                inputMode="decimal"
                placeholder="Amount (₹)"
                value={rows[head].rupees}
                onChange={(e) => update(head, { rupees: e.target.value })}
                className="max-w-xs"
              />
            )}
            {head === "HRA" && (
              <div className="grid gap-2 sm:grid-cols-2">
                <Input
                  placeholder="Annual rent paid (₹)"
                  inputMode="decimal"
                  value={rows.HRA.hraRentRupees}
                  onChange={(e) => update("HRA", { hraRentRupees: e.target.value })}
                />
                <Input
                  placeholder="Landlord name"
                  value={rows.HRA.hraLandlordName}
                  onChange={(e) => update("HRA", { hraLandlordName: e.target.value })}
                />
                <Input
                  placeholder="Landlord PAN (required if rent > ₹1,00,000)"
                  value={rows.HRA.hraLandlordPan}
                  onChange={(e) => update("HRA", { hraLandlordPan: e.target.value })}
                />
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={rows.HRA.hraIsMetro}
                    onChange={(e) => update("HRA", { hraIsMetro: e.target.checked })}
                  />
                  Metro City (Delhi / Mumbai / Kolkata / Chennai)
                </label>
              </div>
            )}
          </div>
        ))}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => save(false)}
            disabled={setItems.isPending}
          >
            Save Draft
          </Button>
          <Button onClick={() => save(true)} disabled={setItems.isPending || submit.isPending}>
            {(setItems.isPending || submit.isPending) && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Submit Declaration
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ProofWorkflow({
  fy,
  declaration,
}: {
  fy: number;
  declaration: import("@/types/tax-declaration").Declaration;
}) {
  const submitProofs = useSubmitMyProofs(fy);
  const canSubmitProofs = declaration.status === "proofs_open";
  const finalised = declaration.status === "finalised";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span>Proofs — {fyLabel(fy)}</span>
          <StatusBadge status={declaration.status} />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {finalised && (
          <p className="text-sm text-muted-foreground">
            Your declaration has been verified and finalised by HR. The verified
            amounts now drive your TDS true-up.
          </p>
        )}
        <Accordion type="multiple" className="w-full">
          {declaration.items.map((item) => (
            <ProofItem key={item.id} fy={fy} item={item} readOnly={finalised} />
          ))}
        </Accordion>
        {canSubmitProofs && (
          <Button
            onClick={() =>
              submitProofs.mutate(undefined, {
                onSuccess: () => toast.success("Submitted for HR review"),
                onError: (e) => toast.error(toApiError(e).message),
              })
            }
            disabled={submitProofs.isPending}
          >
            {submitProofs.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit All Proofs for Review
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function ProofItem({
  fy,
  item,
  readOnly,
}: {
  fy: number;
  item: DeclarationItem;
  readOnly: boolean;
}) {
  const upload = useUploadProof(fy);
  const del = useDeleteProof(fy);
  const headMeta = DECLARATION_HEADS.find((h) => h.head === item.head);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    upload.mutate(
      { itemId: item.id, file },
      {
        onSuccess: () => toast.success("Proof uploaded"),
        onError: (err) => toast.error(toApiError(err).message),
      },
    );
    e.target.value = "";
  };

  return (
    <AccordionItem value={String(item.id)}>
      <AccordionTrigger className="text-sm">
        <span className="flex items-center gap-2">
          {headMeta?.label ?? item.head}
          <Badge variant="outline">{formatPaise(item.declared_paise)}</Badge>
          <StatusBadge status={item.item_status} />
        </span>
      </AccordionTrigger>
      <AccordionContent className="space-y-3">
        {item.verified_paise != null && (
          <p className="text-sm">
            Verified: <strong>{formatPaise(item.verified_paise)}</strong>
          </p>
        )}
        {item.rejection_reason && (
          <p className="text-sm text-destructive">Rejected: {item.rejection_reason}</p>
        )}
        <ul className="space-y-1">
          {item.proofs.map((p) => (
            <li key={p.id} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                {p.file_name}
              </span>
              {!readOnly && item.item_status !== "verified" && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    del.mutate(p.id, {
                      onSuccess: () => toast.success("Proof removed"),
                      onError: (err) => toast.error(toApiError(err).message),
                    })
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </li>
          ))}
          {item.proofs.length === 0 && (
            <li className="text-sm text-muted-foreground">No proofs uploaded yet.</li>
          )}
        </ul>
        {!readOnly && item.item_status !== "verified" && (
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-primary">
            <Upload className="h-4 w-4" />
            <span>{upload.isPending ? "Uploading…" : "Upload Proof (PDF / JPG / PNG)"}</span>
            <input
              type="file"
              className="hidden"
              accept="application/pdf,image/jpeg,image/png"
              onChange={onFile}
              disabled={upload.isPending}
            />
          </label>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
